import type { Sarf39Snapshot } from "./sarf39-model";

const databaseName = "anl-sarf39-offline";
const storeName = "plan";
const recordKey = "week-39";

type OfflineRecord = {
  expiresAt: number;
  snapshot: Sarf39Snapshot | null;
};

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(databaseName, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(storeName);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function readRecord(): Promise<OfflineRecord | null> {
  const database = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(storeName, "readonly");
      const request = transaction.objectStore(storeName).get(recordKey);
      request.onsuccess = () => resolve((request.result as OfflineRecord | undefined) ?? null);
      request.onerror = () => reject(request.error);
    });
  } finally {
    database.close();
  }
}

async function writeRecord(record: OfflineRecord | null): Promise<void> {
  const database = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(storeName, "readwrite");
      if (record) transaction.objectStore(storeName).put(record, recordKey);
      else transaction.objectStore(storeName).delete(recordKey);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally {
    database.close();
  }
}

export async function saveOfflineGrant(expiresAtIso: string): Promise<void> {
  const expiresAt = Date.parse(expiresAtIso);
  if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) return;
  await writeRecord({ expiresAt, snapshot: null });
}

export async function saveOfflineSnapshot(snapshot: Sarf39Snapshot): Promise<void> {
  const record = await readRecord();
  if (!record || record.expiresAt <= Date.now()) return;
  await writeRecord({ ...record, snapshot });
}

export async function readOfflineSnapshot(): Promise<Sarf39Snapshot | null> {
  const record = await readRecord();
  if (!record) return null;
  if (record.expiresAt <= Date.now()) {
    await writeRecord(null);
    return null;
  }
  if (!record.snapshot || !Array.isArray(record.snapshot.products) || !record.snapshot.source) return null;
  return record.snapshot;
}

export function clearOfflineSnapshot(): Promise<void> {
  return writeRecord(null);
}

function addHeadLink(rel: string, href: string) {
  const link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`) ?? document.createElement("link");
  link.rel = rel;
  link.href = href;
  if (!link.isConnected) document.head.append(link);
}

function addHeadMeta(name: string, content: string) {
  const meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`) ?? document.createElement("meta");
  meta.name = name;
  meta.content = content;
  if (!meta.isConnected) document.head.append(meta);
}

export function configureSarf39Install() {
  document.title = "39. Hafta Sarf | ANL Gıda";
  document.querySelector('meta[name="description"]')?.setAttribute("content", "39. hafta üretim planı ve mamul bazında sarf ihtiyacı.");
  addHeadLink("manifest", "/sarf39.webmanifest");
  addHeadLink("apple-touch-icon", "/anl-logo-email.png");
  addHeadMeta("theme-color", "#12344d");
  addHeadMeta("apple-mobile-web-app-capable", "yes");
  addHeadMeta("apple-mobile-web-app-title", "Sarf 39");
  addHeadMeta("apple-mobile-web-app-status-bar-style", "default");
}

export async function prepareOfflineShell(): Promise<void> {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
  const registration = await navigator.serviceWorker.register("/sarf39-sw.js", { scope: "/sarf39" });
  const active = registration.active ?? (await navigator.serviceWorker.ready).active;
  if (!active) return;

  const assets = [...document.querySelectorAll<HTMLScriptElement | HTMLLinkElement>("script[src], link[rel=stylesheet][href]")]
    .map((element) => "src" in element ? element.src : element.href)
    .filter((href) => {
      const url = new URL(href);
      return url.origin === location.origin && url.pathname.startsWith("/assets/");
    });

  await new Promise<void>((resolve, reject) => {
    const channel = new MessageChannel();
    const timeout = window.setTimeout(() => {
      channel.port1.close();
      reject(new Error("Sarf39 sayfa dosyaları önbelleğe alınamadı."));
    }, 10_000);
    channel.port1.onmessage = (event: MessageEvent<{ ok?: boolean }>) => {
      window.clearTimeout(timeout);
      channel.port1.close();
      if (event.data?.ok) resolve();
      else reject(new Error("Sarf39 sayfa dosyaları önbelleğe alınamadı."));
    };
    active.postMessage({ type: "CACHE_SARF39_SHELL", assets }, [channel.port2]);
  });
}
